import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronRight, ChevronLeft, Clock, Video, MapPin, Upload, X, FileText, Film, FolderOpen, ImageIcon, AlertCircle, CalendarDays } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import NativeSelect from "./NativeSelect";
import { INDUSTRY_TYPES, AUDIENCE_REACH, countries } from "../mock";
import { useGHLSlots } from "../hooks/useGHLSlots";
import { formatTime } from "../lib/ghl";
import { getLeadSource } from "../lib/leadTracker";
import axios from "axios";
import { toast } from "sonner";

const GHL_WEBHOOK_URL = process.env.REACT_APP_GHL_BOOKING_WEBHOOK_URL;
const N8N_UPLOAD_WEBHOOK_URL = process.env.REACT_APP_N8N_UPLOAD_WEBHOOK_URL;
const BRAND_NAME = process.env.REACT_APP_BRAND_NAME || "producers.today";

const STEPS = [
  { id: 1, name: "Personal Info" },
  { id: 2, name: "Book Interview" },
  { id: 3, name: "Media Release" },
];

// ─── File quality validator (runs client-side before upload) ────────────────
function validateFile(file) {
  return new Promise((resolve) => {
    if (file.type.startsWith("image/")) {
      if (file.size < 300 * 1024) {
        resolve({
          valid: false,
          message: `Too small: ${(file.size / 1024).toFixed(0)} KB (min 300 KB)`,
        });
        return;
      }
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const shorter = Math.min(img.width, img.height);
        if (shorter < 1000) {
          resolve({
            valid: false,
            message: `Low res: ${img.width}×${img.height}px (min 1000px shortest side)`,
          });
        } else {
          resolve({ valid: true, message: `${img.width}×${img.height}px` });
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ valid: false, message: "Could not read image" });
      };
      img.src = url;
    } else if (file.type.startsWith("video/")) {
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB < 5) {
        resolve({
          valid: false,
          message: `${sizeMB.toFixed(1)} MB — min 5 MB for HD quality`,
        });
      } else {
        resolve({ valid: true, message: `${sizeMB.toFixed(1)} MB` });
      }
    } else {
      // PDFs and docs — accepted as-is
      resolve({ valid: true, message: "" });
    }
  });
}

// Extracts the Google Drive folder ID from a shareable folder URL
function extractFolderId(url) {
  if (!url) return "";
  const foldersMatch = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (foldersMatch) return foldersMatch[1];
  const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (idMatch) return idMatch[1];
  if (!url.includes("/") && !url.includes(".")) return url;
  return "";
}

const cleanDomain = (value) => {
  let val = value.trim();
  if (val.startsWith("https://")) {
    val = val.substring(8);
  } else if (val.startsWith("http://")) {
    val = val.substring(7);
  }
  const slashIndex = val.indexOf("/");
  if (slashIndex !== -1) {
    val = val.substring(0, slashIndex);
  }
  return val;
};

const mapCountryToCode = (country) => {
  const map = {
    "USA": "US",
    "United States": "US",
    "Canada": "CA",
    "United Kingdom": "GB",
    "Australia": "AU",
    "Mexico": "MX",
    "Germany": "DE",
    "France": "FR",
    "Spain": "ES",
    "Italy": "IT",
    "Brazil": "BR",
    "Other": "",
  };
  return map[country] || country;
};

const formatGhlDateTime = (isoString, targetTimezone) => {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: targetTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const parts = formatter.formatToParts(date);
    const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
    
    const month = partMap.month;
    const day = partMap.day;
    const year = partMap.year;
    const hour = partMap.hour;
    const minute = partMap.minute;
    const dayPeriod = (partMap.dayPeriod || "").toUpperCase();

    return {
      dateStr: `${year}-${month}-${day}`,
      timeStr: `${hour}:${minute} ${dayPeriod}`,
      timezone: targetTimezone
    };
  } catch (err) {
    console.error("Error formatting datetime for GHL:", err);
    return null;
  }
};

export default function ApplicationForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headshotFiles, setHeadshotFiles] = useState([]);
  const [logoFiles, setLogoFiles] = useState([]);
  const [promoClipFiles, setPromoClipFiles] = useState([]);
  const [otherAssetFiles, setOtherAssetFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("idle"); 
  const [uploadProgress, setUploadProgress] = useState(0); 
  const [fileValidation, setFileValidation] = useState({}); 
  const [existingFolderId, setExistingFolderId] = useState(""); 
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    producerName: "",
    agencyName: "",
    imoAffiliation: "",
    city: "",
    state: "",
    country: "United States",
    industry: "",
    industryOther: "",
    audienceReach: "",
    bookingDate: null,
    bookingTime: null,
    bookingSlot: null,
    bio: "",
    signature: "",
    agreeRelease: false,
    agreeBroadcast: false,
    driveFolderUrl: "",
    driveFolderId: "",
    websiteUrl: "",
    noWebsite: false,
    instagram: "",
    noInstagram: false,
    linkedIn: "",
    noLinkedIn: false,
    youtube: "",
    noYoutube: false,
    facebook: "",
    noFacebook: false,
    twitter: "",
    noTwitter: false,
    tiktok: "",
    noTikTok: false,
    newsletter: true,
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const resetUploadState = () => {
    setUploadStatus("idle");
    setUploadProgress(0);
    setForm((p) => ({ ...p, driveFolderUrl: "", driveFolderId: "" }));
  };

  const handleHeadshotFilesChange = (updater) => {
    setHeadshotFiles(updater);
    resetUploadState();
  };

  const handleLogoFilesChange = (updater) => {
    setLogoFiles(updater);
    resetUploadState();
  };

  const handlePromoClipFilesChange = (updater) => {
    setPromoClipFiles(updater);
    resetUploadState();
  };

  const handleOtherAssetFilesChange = (updater) => {
    setOtherAssetFiles(updater);
    resetUploadState();
  };

  useEffect(() => {
    const allFiles = [...headshotFiles, ...logoFiles, ...promoClipFiles, ...otherAssetFiles];
    if (allFiles.length === 0) {
      setFileValidation({});
      return;
    }
    let cancelled = false;
    const validate = async () => {
      const results = {};
      for (const file of allFiles) {
        results[`${file.name}-${file.size}`] = await validateFile(file);
      }
      if (!cancelled) setFileValidation(results);
    };
    validate();
    return () => { cancelled = true; };
  }, [headshotFiles, logoFiles, promoClipFiles, otherAssetFiles]);

  const validateStep1 = () => {
    const req = ["firstName", "lastName", "email", "phone", "producerName", "agencyName", "industry", "audienceReach"];
    const fieldNames = {
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      phone: "Phone Number",
      producerName: "Licensed Producer Name",
      agencyName: "Agency / Firm Name",
      industry: "Production Focus",
      audienceReach: "Annual Annuity Production",
    };
    for (const k of req) {
      if (!form[k]) {
        toast.error(`Please fill the required ${fieldNames[k] || k} field.`);
        return false;
      }
    }

    if (form.industry === "Other" && !form.industryOther) {
      toast.error("Please specify your Production Focus.");
      return false;
    }

    // Website URL check
    if (!form.noWebsite) {
      if (!form.websiteUrl) {
        toast.error("Please fill the required Website URL field.");
        return false;
      }
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      if (!urlPattern.test(form.websiteUrl)) {
        toast.error("Please enter a valid website URL.");
        return false;
      }
    }

    // Social fields check
    const socials = [
      { key: "instagram", label: "Instagram", toggle: "noInstagram" },
      { key: "linkedIn", label: "LinkedIn", toggle: "noLinkedIn" },
      { key: "youtube", label: "YouTube", toggle: "noYoutube" },
      { key: "facebook", label: "Facebook", toggle: "noFacebook" },
      { key: "twitter", label: "Twitter", toggle: "noTwitter" },
      { key: "tiktok", label: "TikTok", toggle: "noTikTok" },
    ];

    for (const social of socials) {
      if (!form[social.toggle] && !form[social.key]) {
        toast.error(`Please enter your ${social.label} handle or check "I don't have this".`);
        return false;
      }
    }

    // Verify that at least one social media channel is filled
    const hasAtLeastOneSocial = socials.some(social => !form[social.toggle] && form[social.key]);
    if (!hasAtLeastOneSocial) {
      toast.error("Please fill out at least one social media field.");
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!validateStep1()) return;

      const hasHeadshot = headshotFiles.some((f) => f.type.startsWith("image/"));
      if (!hasHeadshot) {
        toast.error("Please upload at least one professional headshot image to continue.");
        return;
      }
      const hasQualityErrors = Object.values(fileValidation).some((v) => !v.valid);
      if (hasQualityErrors) {
        toast.error("Some files don't meet quality requirements. Please replace or remove them.");
        return;
      }
      const totalFilesCount = headshotFiles.length + logoFiles.length + promoClipFiles.length + otherAssetFiles.length;
      if (totalFilesCount > 0 && uploadStatus !== "success") {
        setUploadStatus("uploading");
        setUploadProgress(0);
        try {
          const fd = new FormData();
          fd.append("firstName", form.firstName);
          fd.append("lastName", form.lastName);
          fd.append("email", form.email);
          fd.append("phone", form.phone);
          fd.append("producerName", form.producerName);
          fd.append("agencyName", form.agencyName);
          fd.append("imoAffiliation", form.imoAffiliation || "");
          fd.append("city", form.city || "");
          fd.append("state", form.state || "");
          fd.append("country", form.country || "");
          fd.append("industry", form.industry === "Other" ? form.industryOther : form.industry);
          fd.append("audienceReach", form.audienceReach);
          fd.append("websiteUrl", form.noWebsite ? "" : cleanDomain(form.websiteUrl));
          fd.append("instagram", form.noInstagram ? "N/A" : form.instagram);
          fd.append("linkedIn", form.noLinkedIn ? "N/A" : form.linkedIn);
          fd.append("youtube", form.noYoutube ? "N/A" : form.youtube);
          fd.append("facebook", form.noFacebook ? "N/A" : form.facebook);
          fd.append("twitter", form.noTwitter ? "N/A" : form.twitter);
          fd.append("tiktok", form.noTikTok ? "N/A" : form.tiktok);
          fd.append("socialHandles", (() => {
            const activeSocials = [];
            if (!form.noInstagram && form.instagram) activeSocials.push(`Instagram: ${form.instagram}`);
            if (!form.noLinkedIn && form.linkedIn) activeSocials.push(`LinkedIn: ${form.linkedIn}`);
            if (!form.noYoutube && form.youtube) activeSocials.push(`YouTube: ${form.youtube}`);
            if (!form.noFacebook && form.facebook) activeSocials.push(`Facebook: ${form.facebook}`);
            if (!form.noTwitter && form.twitter) activeSocials.push(`Twitter: ${form.twitter}`);
            if (!form.noTikTok && form.tiktok) activeSocials.push(`TikTok: ${form.tiktok}`);
            return activeSocials.length > 0 ? activeSocials.join(", ") : "None";
          })());
          if (existingFolderId) {
            fd.append("folderId", existingFolderId);
          }

          const fileMetadata = {};
          headshotFiles.forEach((file) => {
            const filename = `Professional Headshots___${file.name}`;
            fd.append("files[]", file, filename);
            fileMetadata[file.name] = "Professional Headshots";
          });
          logoFiles.forEach((file) => {
            const filename = `Logos___${file.name}`;
            fd.append("files[]", file, filename);
            fileMetadata[file.name] = "Logos";
          });
          promoClipFiles.forEach((file) => {
            const filename = `Promo Clips___${file.name}`;
            fd.append("files[]", file, filename);
            fileMetadata[file.name] = "Promo Clips";
          });
          otherAssetFiles.forEach((file) => {
            const filename = `Other Assets___${file.name}`;
            fd.append("files[]", file, filename);
            fileMetadata[file.name] = "Other Assets";
          });
          fd.append("fileMetadata", JSON.stringify(fileMetadata));

          const res = await axios.post(N8N_UPLOAD_WEBHOOK_URL, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120_000,
            onUploadProgress: (e) => {
              const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
              setUploadProgress(pct);
            },
          });
          const responseObj = Array.isArray(res.data) ? res.data[0] : res.data;
          const folderUrl = responseObj?.folderUrl || responseObj?.folder_url || responseObj?.webViewLink || responseObj?.url || "";
          if (!folderUrl) {
            throw new Error("Upload service did not return a confirmation URL.");
          }
          const folderId = responseObj?.folderId || responseObj?.folder_id || responseObj?.id || extractFolderId(folderUrl);
          if (folderId) {
            setExistingFolderId(folderId);
          }
          setForm((p) => ({
            ...p,
            driveFolderUrl: folderUrl,
            driveFolderId: folderId || "",
          }));
          setUploadProgress(100);
          setUploadStatus("success");
          toast.success(existingFolderId ? "Files replaced successfully!" : "Files uploaded successfully!");
        } catch (err) {
          setUploadStatus("error");
          toast.error("File upload failed. Please try again.");
          return;
        }
      }
    }
    if (step === 2) {
      if (!form.bookingDate || !form.bookingTime) {
        toast.error("Please select a date and time for your interview.");
        return;
      }
    }
    if (step === 3) {
      if (!form.agreeRelease || !form.agreeBroadcast) {
        toast.error("Please agree to the terms to continue.");
        return;
      }
      setIsSubmitting(true);
      const loadingToast = toast.loading("Submitting application...");
      
      const leadSource = getLeadSource();
      const sourceString = leadSource
        ? `${BRAND_NAME} Guest Application - ${leadSource}`
        : `${BRAND_NAME} Guest Application`;

      try {
        const selectedTimeRaw = form.bookingSlot;
        const ghlFormatted = formatGhlDateTime(selectedTimeRaw, "America/Indiana/Indianapolis");
        const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localFormatted = formatGhlDateTime(selectedTimeRaw, localTimezone);

        const payload = {
          firstName: form.firstName,
          lastName: form.lastName,
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          phone: form.phone,
          producerName: form.producerName,
          producer: form.producerName,
          agencyName: form.agencyName,
          agency: form.agencyName,
          imoAffiliation: form.imoAffiliation,
          city: form.city,
          state: form.state,
          country: mapCountryToCode(form.country),
          industry: form.industry === "Other" ? form.industryOther : form.industry,
          audienceReach: form.audienceReach,
          agreeRelease: form.agreeRelease,
          agreeBroadcast: form.agreeBroadcast,
          interviewDate: ghlFormatted ? ghlFormatted.dateStr : (form.bookingDate === "Waitlist" ? "Waitlist" : ""),
          interviewTime: ghlFormatted ? ghlFormatted.timeStr : (form.bookingTime === "Waitlist" ? "Waitlist" : ""),
          interviewDateTime: ghlFormatted ? `${ghlFormatted.dateStr} ${ghlFormatted.timeStr}` : (form.bookingSlot === "Waitlist" ? "Waitlist" : ""),
          interviewDateLocal: localFormatted ? localFormatted.dateStr : "",
          interviewTimeLocal: localFormatted ? localFormatted.timeStr : "",
          interviewDateTimeLocal: localFormatted ? `${localFormatted.dateStr} ${localFormatted.timeStr}` : "",
          interviewTimeRaw: selectedTimeRaw || "",
          timezone: localTimezone,
          ghlTimezone: "America/Indiana/Indianapolis",
          newsletter: form.newsletter,
          source: sourceString,
          driveFolderUrl: form.driveFolderUrl,
          driveFolderId: form.driveFolderId || existingFolderId,
          websiteUrl: form.noWebsite ? "" : cleanDomain(form.websiteUrl),
          socialHandles: (() => {
            const activeSocials = [];
            if (!form.noInstagram && form.instagram) activeSocials.push(`Instagram: ${form.instagram}`);
            if (!form.noLinkedIn && form.linkedIn) activeSocials.push(`LinkedIn: ${form.linkedIn}`);
            if (!form.noYoutube && form.youtube) activeSocials.push(`YouTube: ${form.youtube}`);
            if (!form.noFacebook && form.facebook) activeSocials.push(`Facebook: ${form.facebook}`);
            if (!form.noTwitter && form.twitter) activeSocials.push(`Twitter: ${form.twitter}`);
            if (!form.noTikTok && form.tiktok) activeSocials.push(`TikTok: ${form.tiktok}`);
            return activeSocials.length > 0 ? activeSocials.join(", ") : "None";
          })(),
          instagram: form.noInstagram ? "N/A" : form.instagram,
          linkedIn: form.noLinkedIn ? "N/A" : form.linkedIn,
          youtube: form.noYoutube ? "N/A" : form.youtube,
          facebook: form.noFacebook ? "N/A" : form.facebook,
          twitter: form.noTwitter ? "N/A" : form.twitter,
          tiktok: form.noTikTok ? "N/A" : form.tiktok,
          submittedAt: new Date().toISOString(),
        };

        await axios.post(GHL_WEBHOOK_URL, payload);
        toast.dismiss(loadingToast);
        toast.success("Application submitted successfully!");
        setSubmitted(true);
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error("Failed to submit application. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const handleBack = () => setStep((s) => Math.max(s - 1, 1));

  // Live GHL Slots hook call
  const { slots: fetchedSlots, loading: slotsLoading, error: slotsError } = useGHLSlots(14);

  return (
    <section id="apply" className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden">
      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-[#f5c451] text-xs md:text-sm font-semibold tracking-[0.3em] uppercase mb-4">Official Guest Application</div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-white leading-none">APPLY TO BE A <span className="text-blue-bold">FEATURED GUEST</span></h2>
          <p className="mt-5 text-white/65 text-base md:text-lg">Complete all 3 steps to submit your application and book your interview.</p>
        </div>
        <div className="mb-10 md:mb-14">
          <div className="grid grid-cols-3 gap-3 md:gap-5 max-w-4xl mx-auto">
            {STEPS.map((s) => {
              const active = step === s.id;
              const done = step > s.id || (submitted && s.id <= 3);
              return (
                <button key={s.id} type="button" onClick={() => { if (submitted) return; if (done) setStep(s.id); }} className={`relative rounded-md py-4 px-2 md:p-5 text-left transition-all duration-300 border-2 overflow-hidden ${active && !submitted ? "border-[#f5c451] bg-gradient-to-b from-[#f5c451]/[0.06] to-transparent" : done ? "border-[#14b8a6]/45 bg-[#14b8a6]/[0.04] cursor-pointer hover:border-[#14b8a6]/70" : "border-white/8 bg-white/[0.015]"}`}>
                  <div className="flex flex-col items-center gap-2 md:gap-3">
                    <div className={`flex items-center justify-center font-display rounded-md transition-all duration-300 ${active && !submitted ? "w-9 h-9 md:w-11 md:h-11 bg-[#f5c451] text-[#1a0f00] text-xl md:text-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]" : done ? "w-9 h-9 md:w-11 md:h-11 bg-[#14b8a6]/15 text-[#14b8a6] text-xl md:text-2xl border border-[#14b8a6]/40" : "w-9 h-9 md:w-11 md:h-11 bg-white/[0.04] text-white/30 text-xl md:text-2xl border border-white/10"}`}>
                      {done ? <Check className="w-4 h-4 md:w-5 md:h-5" strokeWidth={3} /> : s.id}
                    </div>
                    <div className={`font-display text-[10px] sm:text-xs md:text-sm leading-tight uppercase text-center ${active && !submitted ? "text-white" : done ? "text-white/85" : "text-white/55"}`}>
                      {s.name}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="premium-card rounded-md p-6 md:p-10 lg:p-12 border-t-2 border-t-[#f5c451]/40">
          {submitted ? (
            <SuccessScreen form={form} />
          ) : (
            <>
              {step === 1 && (
                <Step1
                  form={form}
                  update={update}
                  headshotFiles={headshotFiles}
                  onHeadshotFilesChange={handleHeadshotFilesChange}
                  logoFiles={logoFiles}
                  onLogoFilesChange={handleLogoFilesChange}
                  promoClipFiles={promoClipFiles}
                  onPromoClipFilesChange={handlePromoClipFilesChange}
                  otherAssetFiles={otherAssetFiles}
                  onOtherAssetFilesChange={handleOtherAssetFilesChange}
                  uploadStatus={uploadStatus}
                  uploadProgress={uploadProgress}
                  fileValidation={fileValidation}
                  resetUploadState={resetUploadState}
                />
              )}
              {step === 2 && (
                <Step2 
                  form={form} 
                  update={update} 
                  slotsData={fetchedSlots}
                  isLoadingSlots={slotsLoading}
                  slotsError={slotsError}
                />
              )}
              {step === 3 && <Step3 form={form} update={update} />}
              <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-between items-center">
                <Button variant="outline" onClick={handleBack} disabled={step === 1} className="h-12 px-6 border-white/20 bg-transparent text-white hover:bg-white/[0.06] hover:text-white disabled:opacity-30">Back</Button>
                <Button onClick={handleNext} disabled={isSubmitting || uploadStatus === "uploading"} className="btn-gold h-12 px-8 rounded-md font-display tracking-[0.06em] text-sm disabled:opacity-50">
                  {uploadStatus === "uploading" ? <>UPLOADING...</> : isSubmitting ? <>SUBMITTING...</> : <>{step === 1 && <>NEXT: BOOK INTERVIEW</>}{step === 2 && <>NEXT: MEDIA RELEASE</>}{step === 3 && <>SUBMIT APPLICATION</>}<ChevronRight className="ml-2 w-4 h-4" /></>}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Label className="text-[11px] tracking-[0.18em] uppercase text-white/70 font-semibold mb-2 block">
      {children} {required && <span className="text-[#f5c451]">*</span>}
    </Label>
  );
}

const FieldInputWithToggle = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  hasNoneOption = false,
  isNone = false,
  onNoneChange,
  noneLabel = "I don't have this",
  className = ""
}) => {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-[11px] tracking-[0.18em] uppercase text-white/70 font-semibold block">
          {label}
        </Label>
        {hasNoneOption && (
          <div className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              id={`none-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              checked={isNone}
              onCheckedChange={(checked) => onNoneChange(!!checked)}
              className="h-3.5 w-3.5 border-white/20 data-[state=checked]:border-[#f5c451] data-[state=checked]:bg-[#f5c451] data-[state=checked]:text-black"
            />
            <Label
              htmlFor={`none-${label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className="text-[10px] font-display uppercase tracking-wider text-white/40 hover:text-white/60 cursor-pointer select-none"
            >
              {noneLabel}
            </Label>
          </div>
        )}
      </div>
      <Input
        type={type}
        value={isNone ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isNone ? "Not Applicable" : placeholder}
        disabled={isNone}
        className={`h-12 border-white/10 text-white placeholder:text-white/40 focus-visible:border-[#f5c451]/60 focus-visible:ring-2 focus-visible:ring-[#f5c451]/30 focus-visible:ring-offset-0 transition-all ${
          isNone
            ? "bg-white/[0.02] border-white/5 text-white/30 cursor-not-allowed"
            : "bg-white/5"
        }`}
      />
    </div>
  );
};

const FieldSelect = ({ label, value, onChange, options, placeholder, className = "" }) => (
  <div className={className}>
    <Label className="mb-2 block text-[11px] tracking-[0.18em] uppercase text-white/70 font-semibold">
      {label}
    </Label>

    {/* Native select on mobile */}
    <div className="relative sm:hidden">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="premium-input h-12 w-full appearance-none rounded-md px-3 pr-10 text-sm text-white focus:border-[#f5c451]/60 focus:outline-none focus:ring-2 focus:ring-[#f5c451]/30"
      >
        <option value="" disabled hidden>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#0b1110] text-white">
            {opt}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45"
        xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>

    {/* Styled Radix Select on desktop */}
    <div className="hidden sm:block">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="premium-input h-12 border-white/10 bg-white/5 text-white focus:border-[#f5c451]/60 focus:ring-2 focus:ring-[#f5c451]/30 focus:ring-offset-0">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-64 border-white/10 bg-[#0a0a0a] text-white">
          {options.map((opt) => (
            <SelectItem key={opt} value={opt} className="focus:bg-[#f5c451]/10 focus:text-amber-100">
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

function Step1({
  form,
  update,
  headshotFiles,
  onHeadshotFilesChange,
  logoFiles,
  onLogoFilesChange,
  promoClipFiles,
  onPromoClipFilesChange,
  otherAssetFiles,
  onOtherAssetFilesChange,
  uploadStatus,
  uploadProgress,
  fileValidation,
  resetUploadState,
}) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-[#f5c451] tracking-[0.06em]">STEP 1: PERSONAL INFORMATION</h3>
      <p className="text-white/55 text-sm mt-2 mb-8">Tell us about yourself so we can reach you.</p>
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <FieldLabel required>First Name</FieldLabel>
          <Input className="premium-input h-12" placeholder="John" value={form.firstName} onChange={(e) => update("firstName", e.target.value)} />
        </div>
        <div>
          <FieldLabel required>Last Name</FieldLabel>
          <Input className="premium-input h-12" placeholder="Smith" value={form.lastName} onChange={(e) => update("lastName", e.target.value)} />
        </div>
        <div>
          <FieldLabel required>Email Address</FieldLabel>
          <Input type="email" className="premium-input h-12" placeholder="john@example.com" value={form.email} onChange={(e) => update("email", e.target.value)} />
        </div>
        <div>
          <FieldLabel required>Phone Number</FieldLabel>
          <Input className="premium-input h-12" placeholder="+1 (555) 000-0000" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
        </div>
        <div>
          <FieldLabel required>Licensed Producer Name</FieldLabel>
          <Input className="premium-input h-12" placeholder="Writing Agent Name" value={form.producerName} onChange={(e) => update("producerName", e.target.value)} />
        </div>
        <div>
          <FieldLabel required>Agency / Firm Name</FieldLabel>
          <Input className="premium-input h-12" placeholder="e.g. Retirement Solutions Group" value={form.agencyName} onChange={(e) => update("agencyName", e.target.value)} />
        </div>
        <div>
          <FieldLabel>IMO / FMO Affiliation</FieldLabel>
          <Input className="premium-input h-12" placeholder="e.g. Advisors Excel, Impact Partnership" value={form.imoAffiliation} onChange={(e) => update("imoAffiliation", e.target.value)} />
        </div>
        <div>
          <FieldLabel>City</FieldLabel>
          <Input className="premium-input h-12" placeholder="Los Angeles" value={form.city} onChange={(e) => update("city", e.target.value)} />
        </div>
        <div>
          <FieldLabel>State / Province</FieldLabel>
          <Input className="premium-input h-12" placeholder="California" value={form.state} onChange={(e) => update("state", e.target.value)} />
        </div>
        <FieldSelect 
          label="Country" 
          value={form.country} 
          onChange={(v) => update("country", v)} 
          options={countries} 
          placeholder="Select country..." 
          className="md:col-span-2"
        />
        <div>
          <FieldLabel required>Production Focus</FieldLabel>
          <NativeSelect value={form.industry} onChange={(v) => {
            update("industry", v);
            if (v !== "Other") update("industryOther", "");
          }} placeholder="Select focus..." options={INDUSTRY_TYPES} />
        </div>
        <div>
          <FieldLabel required>Annual Annuity Production</FieldLabel>
          <NativeSelect value={form.audienceReach} onChange={(v) => update("audienceReach", v)} placeholder="Select volume..." options={AUDIENCE_REACH} />
        </div>
        {form.industry === "Other" && (
          <div className="md:col-span-2 animate-in fade-in slide-in-from-top-2 duration-200">
            <FieldLabel required>Please Specify Production Focus</FieldLabel>
            <Input className="premium-input h-12" placeholder="e.g. Disability Insurance, Group Benefits" value={form.industryOther} onChange={(e) => update("industryOther", e.target.value)} />
          </div>
        )}
        
        <FieldInputWithToggle
          label="Website URL *"
          value={form.websiteUrl}
          onChange={(v) => update("websiteUrl", v)}
          placeholder="https://example.com"
          hasNoneOption={true}
          isNone={form.noWebsite}
          onNoneChange={(checked) => {
            update("noWebsite", checked);
            if (checked) update("websiteUrl", "");
          }}
          noneLabel="No Website"
          className="md:col-span-2"
        />

        <div className="md:col-span-2 mt-4 mb-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#f5c451]">Social Media Channels</div>
          <p className="text-xs text-white/50 mt-1">
            Please provide handles or URLs for the channels you utilize. Tick "No [Platform]" for any you do not use.
          </p>
        </div>

        <FieldInputWithToggle
          label="Instagram *"
          value={form.instagram}
          onChange={(v) => update("instagram", v)}
          placeholder="@username"
          hasNoneOption={true}
          isNone={form.noInstagram}
          onNoneChange={(checked) => {
            update("noInstagram", checked);
            if (checked) update("instagram", "");
          }}
          noneLabel="No Instagram"
        />

        <FieldInputWithToggle
          label="LinkedIn *"
          value={form.linkedIn}
          onChange={(v) => update("linkedIn", v)}
          placeholder="linkedin.com/in/username"
          hasNoneOption={true}
          isNone={form.noLinkedIn}
          onNoneChange={(checked) => {
            update("noLinkedIn", checked);
            if (checked) update("linkedIn", "");
          }}
          noneLabel="No LinkedIn"
        />

        <FieldInputWithToggle
          label="YouTube *"
          value={form.youtube}
          onChange={(v) => update("youtube", v)}
          placeholder="youtube.com/@channel"
          hasNoneOption={true}
          isNone={form.noYoutube}
          onNoneChange={(checked) => {
            update("noYoutube", checked);
            if (checked) update("youtube", "");
          }}
          noneLabel="No YouTube"
        />

        <FieldInputWithToggle
          label="Facebook *"
          value={form.facebook}
          onChange={(v) => update("facebook", v)}
          placeholder="facebook.com/username"
          hasNoneOption={true}
          isNone={form.noFacebook}
          onNoneChange={(checked) => {
            update("noFacebook", checked);
            if (checked) update("facebook", "");
          }}
          noneLabel="No Facebook"
        />

        <FieldInputWithToggle
          label="Twitter / X *"
          value={form.twitter}
          onChange={(v) => update("twitter", v)}
          placeholder="x.com/username"
          hasNoneOption={true}
          isNone={form.noTwitter}
          onNoneChange={(checked) => {
            update("noTwitter", checked);
            if (checked) update("twitter", "");
          }}
          noneLabel="No Twitter/X"
        />

        <FieldInputWithToggle
          label="TikTok *"
          value={form.tiktok}
          onChange={(v) => update("tiktok", v)}
          placeholder="tiktok.com/@username"
          hasNoneOption={true}
          isNone={form.noTikTok}
          onNoneChange={(checked) => {
            update("noTikTok", checked);
            if (checked) update("tiktok", "");
          }}
          noneLabel="No TikTok"
        />

        <div className="md:col-span-2 mt-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#f5c451]">Asset Upload Requirements</div>
          <p className="text-xs text-white/50 mt-1">Upload your files below. They will be automatically organized into subfolders inside Google Drive.</p>
        </div>
        <MediaUpload label="Professional Headshots *" description="Quality check: Image ≥ 300 KB, shortest side ≥ 1000px. At least one image is required." accept="image/*" files={headshotFiles} onFilesChange={onHeadshotFilesChange} uploadStatus={uploadStatus} fileValidation={fileValidation} />
        <MediaUpload label="Logos" description="PNG or SVG format preferred. Optional." accept="image/*" files={logoFiles} onFilesChange={onLogoFilesChange} uploadStatus={uploadStatus} fileValidation={fileValidation} />
        <MediaUpload label="Promo Clips" description="Short video clips or highlights. Quality check: Video ≥ 5 MB. Optional." accept="video/*" files={promoClipFiles} onFilesChange={onPromoClipFilesChange} uploadStatus={uploadStatus} fileValidation={fileValidation} />
        <MediaUpload label="Other Assets / PDFs" description="Media kits, PDFs, or other relevant materials. Optional." accept="*" files={otherAssetFiles} onFilesChange={onOtherAssetFilesChange} uploadStatus={uploadStatus} fileValidation={fileValidation} />
        
        {/* Unified progress bar */}
        {uploadStatus === "uploading" && (
          <div className="md:col-span-2 rounded-md border border-[#f5c451]/20 bg-[#f5c451]/[0.03] p-6 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-[#f5c451] border-t-transparent rounded-full animate-spin"></div><span className="text-sm font-bold text-[#f5c451] uppercase tracking-wider">Uploading Assets...</span></div>
              <span className="text-sm font-black text-[#f5c451]">{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#f5c451] to-amber-300 rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} /></div>
            <p className="text-white/40 text-[10px] mt-2.5 text-center">Please wait while we create your folders and upload files to Google Drive. This may take a moment.</p>
          </div>
        )}

        {/* Unified success banner */}
        {uploadStatus === "success" && form.driveFolderUrl && (
          <div className="md:col-span-2 rounded-md border border-[#14b8a6]/45 bg-[#14b8a6]/[0.06] p-5 flex flex-col sm:flex-row items-center gap-4 mt-4">
            <div className="w-12 h-12 rounded-md bg-[#14b8a6]/15 border border-[#14b8a6]/30 flex items-center justify-center flex-shrink-0"><FolderOpen className="w-5 h-5 text-[#14b8a6]" /></div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="text-[#14b8a6] text-sm font-bold mb-1">Assets Uploaded Successfully! ✓</div>
              <p className="text-white/60 text-xs leading-relaxed mb-2">All your media files have been organized into dedicated subfolders inside your main Google Drive folder.</p>
              <a href={form.driveFolderUrl} target="_blank" rel="noopener noreferrer" className="text-[#f5c451] text-xs hover:text-amber-300 underline truncate block transition-colors">{form.driveFolderUrl}</a>
            </div>
            <button type="button" onClick={resetUploadState} className="text-xs font-bold text-white bg-white/10 hover:bg-white/15 px-4 py-2.5 rounded-md border border-white/10 transition-colors uppercase tracking-wider">Modify Assets</button>
          </div>
        )}

        {/* Unified error banner */}
        {uploadStatus === "error" && (
          <div className="md:col-span-2 rounded-md border border-red-500/30 bg-red-500/[0.04] p-5 flex items-center gap-3 mt-4">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-red-400 text-sm font-bold">Upload Failed</div>
              <p className="text-white/50 text-xs mt-0.5">Some files could not be uploaded. Please verify your connection and try again.</p>
            </div>
            <button type="button" onClick={resetUploadState} className="text-xs font-bold text-red-400 hover:text-red-300 transition-colors uppercase tracking-wider px-3 py-1.5 hover:bg-white/5 rounded border border-red-500/20">Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Step2({ form, update, slotsData, isLoadingSlots, slotsError }) {
  const selectedDate = form.bookingDate;
  const selectedTime = form.bookingTime;

  const getAvailableDates = () => {
    if (!slotsData) return [];
    const dates = Object.keys(slotsData).filter(key => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
      return slotsData[key] && slotsData[key].length > 0;
    });
    return dates.sort();
  };

  const availableDates = getAvailableDates();

  useEffect(() => {
    if (!isLoadingSlots && slotsData && availableDates.length === 0) {
      update("bookingDate", "Waitlist");
      update("bookingTime", "Waitlist");
      update("bookingSlot", "Waitlist");
    }
  }, [isLoadingSlots, slotsData, availableDates.length, update]);

  const getSelectedDateKey = () => {
    if (!selectedDate || selectedDate === "Waitlist") return "";
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const selectedDateKey = getSelectedDateKey();

  let availableTimes = [];
  if (selectedDate && selectedDate !== "Waitlist" && slotsData) {
    const daySlots = slotsData[selectedDateKey] || [];
    availableTimes = daySlots.map(slotStr => {
      const slotDate = new Date(slotStr);
      const timeFormatted = slotDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
      return { time: timeFormatted, raw: slotStr };
    });
  }

  const formatDateKey = (key) => {
    const d = new Date(key + "T00:00:00");
    return {
      weekday: d.toLocaleDateString("en", { weekday: "short" }),
      day: d.getDate(),
      month: d.toLocaleDateString("en", { month: "short" }),
    };
  };

  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-[#f5c451] tracking-[0.06em]">STEP 2: BOOK YOUR INTERVIEW</h3>
      <p className="text-white/55 text-sm mt-2 mb-8">Select your preferred date and time. All times are 30-minute sessions via Google Meet.</p>
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10">
        <div className="border-r-0 lg:border-r border-white/5 lg:pr-8">
          <div className="rounded-sm border border-white/10 bg-black/30 p-5 mb-5">
            <div className="font-display text-xl text-white mb-1">Producers Today Interview</div>
            <div className="text-white/55 text-sm mb-4">Featured Guest Session</div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2.5 text-white/75"><Clock className="w-4 h-4 text-[#f5c451]" /> <span>30 mins</span></div>
              <div className="flex items-center gap-2.5 text-white/75"><Video className="w-4 h-4 text-[#f5c451]" /> <span>via Google Meet</span></div>
              <div className="flex items-center gap-2.5 text-white/75"><MapPin className="w-4 h-4 text-[#f5c451]" /> <span>Meeting details will be sent to the email</span></div>
            </div>
          </div>
          <p className="text-white/55 text-sm leading-relaxed">Our producers will record a high-quality session that will be edited and distributed across our podcast and television networks. You&apos;ll receive a finished promo clip within 14 days of recording.</p>
        </div>
        <div>
          {isLoadingSlots && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <div className="w-8 h-8 border-2 border-[#f5c451] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-white/55">Loading available slots...</p>
            </div>
          )}

          {!isLoadingSlots && slotsError && (
            <div className="rounded-sm border border-red-400/20 bg-red-400/5 p-5 text-center text-sm text-red-300/80">
              Could not load calendar slots. Please try again later.
            </div>
          )}

          {!isLoadingSlots && !slotsError && slotsData && (
            <>
              {availableDates.length === 0 ? (
                <div className="rounded-sm border border-[#f5c451]/20 bg-[#f5c451]/5 p-6 text-center">
                  <div className="text-[#f5c451] font-bold uppercase tracking-[0.2em] text-xs">All Slots Booked</div>
                  <h4 className="mt-3 text-lg font-bold uppercase text-white">Priority Waitlist Flow Active</h4>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-md mx-auto">
                    Our interview slots are fully booked for the next 2 weeks. You can proceed with your application, and we will place you on the Priority Waitlist.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#f5c451]/10 px-4 py-2 text-xs font-bold text-[#f5c451] border border-[#f5c451]/30">
                    ✨ You will be automatically waitlisted upon submission
                  </div>
                </div>
              ) : (
                <>
                  {selectedDate === "Waitlist" ? (
                    <div className="rounded-sm border border-[#f5c451]/20 bg-[#f5c451]/5 p-6 text-center">
                      <div className="text-[#f5c451] font-bold uppercase tracking-[0.2em] text-xs">Priority Waitlist</div>
                      <h4 className="mt-3 text-lg font-bold uppercase text-white">You have chosen the Waitlist</h4>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-md mx-auto">
                        You can proceed to submit your application, and we will put you on the Priority Waitlist. If you change your mind and want to book a live slot, click below.
                      </p>
                      <button
                        type="button"
                        onClick={() => { update("bookingDate", null); update("bookingTime", null); update("bookingSlot", null); }}
                        className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#f5c451] px-4 py-2 text-xs font-bold text-black hover:bg-amber-300 transition-all"
                      >
                        ← Book an Available Time Slot
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Date grid */}
                      <div>
                        <div className="mb-4 flex items-center justify-between">
                          <Label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">Date *</Label>
                          <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#f5c451]/60">
                            <CalendarDays className="h-3 w-3" /> Live from calendar
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                          {availableDates.map((key) => {
                            const { weekday, day, month } = formatDateKey(key);
                            const active = selectedDateKey === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => { update("bookingDate", new Date(key + "T00:00:00")); update("bookingTime", null); update("bookingSlot", null); }}
                                className={`flex flex-col items-center rounded-md border p-2.5 transition-all ${active
                                  ? "border-[#f5c451]/70 bg-[#f5c451]/10 shadow-[0_8px_24px_-12px_rgba(245,196,81,0.5)]"
                                  : "border-white/10 bg-white/5 hover:border-[#f5c451]/40 hover:bg-white/10"
                                  }`}
                              >
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${active ? "text-[#f5c451]" : "text-white/40"}`}>{weekday}</span>
                                <span className={`mt-0.5 text-xl font-black ${active ? "text-white" : "text-white/80"}`}>{day}</span>
                                <span className={`text-[9px] font-semibold uppercase ${active ? "text-[#f5c451]" : "text-white/40"}`}>{month}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Time slots */}
                      {selectedDate && (
                        <div className="mt-6">
                          <Label className="mb-3 block text-[11px] font-bold uppercase tracking-[0.18em] text-white/50">Time *</Label>
                          {availableTimes.length === 0 ? (
                            <p className="text-sm text-white/40">No time slots available for this date.</p>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                              {availableTimes.map((slot) => {
                                const active = selectedTime === slot.time;
                                return (
                                  <button
                                    key={slot.time}
                                    type="button"
                                    onClick={() => { update("bookingTime", slot.time); update("bookingSlot", slot.raw); }}
                                    className={`rounded-md border px-3 py-2.5 text-sm font-bold transition-all ${active
                                      ? "border-[#f5c451]/70 bg-[#f5c451] text-black"
                                      : "border-white/10 bg-white/5 text-white/70 hover:border-[#f5c451]/40 hover:text-[#f5c451]"
                                      }`}
                                  >
                                    {slot.time}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {!selectedDate && (
                        <p className="mt-4 text-sm text-white/30">← Select a date above to see available times</p>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Step3({ form, update }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-[#f5c451] tracking-[0.06em]">STEP 3: MEDIA RELEASE AGREEMENT</h3>
      <p className="text-white/55 text-sm mt-2 mb-8">Please review and acknowledge the broadcast and media release terms.</p>
      <div className="rounded-sm bg-black/40 border border-white/10 p-5 md:p-6 mb-6 text-white/65 text-sm leading-relaxed max-h-64 overflow-y-auto">
        <p className="mb-3">By signing below, I grant Producers Today Magazine &amp; Television and Motivation &amp; Success Network the irrevocable right to record, edit, broadcast, distribute, and reproduce my appearance across podcast, television, streaming, and online platforms worldwide, in perpetuity.</p>
        <p className="mb-3">I confirm that any statements, opinions, or representations I make during the interview are my own. I release Producers Today and its affiliates from any liability associated with the broadcast, publication, or distribution of the recorded content.</p>
        <p>I understand that there is <span className="text-[#f5c451] font-semibold">no cost to me</span>, no application fee, and no appearance fee, and that I may request changes to my featured episode within 14 days of the recording date.</p>
      </div>
      <div className="space-y-4 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={form.agreeRelease} onCheckedChange={(v) => update("agreeRelease", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#f5c451] data-[state=checked]:border-[#f5c451] data-[state=checked]:text-black" />
          <span className="text-white/80 text-sm">I agree to the Media Release terms and grant permission to broadcast my appearance.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={form.agreeBroadcast} onCheckedChange={(v) => update("agreeBroadcast", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#f5c451] data-[state=checked]:border-[#f5c451] data-[state=checked]:text-black" />
          <span className="text-white/80 text-sm">I confirm that all information provided is accurate and that I am at least 18 years of age.</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer mt-2">
          <Checkbox checked={form.newsletter} onCheckedChange={(v) => update("newsletter", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#f5c451] data-[state=checked]:border-[#f5c451] data-[state=checked]:text-black" />
          <span className="text-white/80 text-sm">Subscribe to the Producers Today™ newsletter for news, highlights, and guest spotlights.</span>
        </label>
      </div>
    </div>
  );
}

function MediaUpload({ label, description, accept, files, onFilesChange, uploadStatus, fileValidation }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploadStatus === "uploading") return;
    const dropped = Array.from(e.dataTransfer.files);
    const filtered = dropped.filter((file) => {
      if (!accept || accept === "*") return true;
      if (accept.endsWith("/*")) {
        const typePrefix = accept.split("/")[0];
        return file.type.startsWith(typePrefix + "/");
      }
      return file.type.match(new RegExp(accept.replace("*", ".*")));
    });
    onFilesChange((prev) => [...prev, ...filtered]);
  };

  const handleDragOver = (e) => { e.preventDefault(); if (uploadStatus !== "uploading") setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleSelect = (e) => { const selected = Array.from(e.target.files || []); onFilesChange((prev) => [...prev, ...selected]); e.target.value = ""; };
  const removeFile = (index) => { onFilesChange((prev) => prev.filter((_, i) => i !== index)); };
  const formatSize = (bytes) => { if (bytes < 1024) return `${bytes} B`; if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; };
  const getFileIcon = (file) => { if (file.type.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-blue-400" />; if (file.type.startsWith("video/")) return <Film className="w-4 h-4 text-purple-400" />; return <FileText className="w-4 h-4 text-orange-400" />; };
  const isDisabled = uploadStatus === "uploading" || uploadStatus === "success";

  return (
    <div className="mt-2 flex flex-col">
      <Label className="text-[11px] tracking-[0.18em] uppercase text-white/70 font-semibold mb-2 block">{label}</Label>
      {description && (
        <div className="flex items-start gap-2 rounded-md bg-[#f5c451]/[0.03] border border-[#f5c451]/15 px-3 py-2 mb-3">
          <AlertCircle className="w-3.5 h-3.5 text-[#f5c451]/60 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/50 leading-relaxed">{description}</p>
        </div>
      )}
      {uploadStatus !== "success" && (
        <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={isDisabled ? undefined : () => inputRef.current?.click()} className={`rounded-md border-2 border-dashed p-6 text-center transition-all duration-200 ${isDisabled ? "opacity-60 cursor-not-allowed border-white/10 bg-white/[0.01]" : isDragging ? "border-[#f5c451]/60 bg-[#f5c451]/[0.06] cursor-copy" : uploadStatus === "error" ? "border-red-500/40 bg-red-500/[0.03] hover:border-red-500/60 cursor-pointer" : "border-white/15 bg-white/[0.015] hover:border-white/30 hover:bg-white/[0.03] cursor-pointer"}`}>
          <input ref={inputRef} type="file" multiple accept={accept} onChange={handleSelect} className="hidden" disabled={isDisabled} />
          {uploadStatus === "uploading" ? (
            <><Upload className="w-6 h-6 mx-auto mb-2 text-[#f5c451]" /><div className="text-[#f5c451] text-sm font-medium">Uploading...</div></>
          ) : (
            <><Upload className="w-6 h-6 mx-auto mb-2 text-white/30" /><div className="text-white/60 text-xs font-medium">{isDragging ? "Drop files here" : "Drag files here or click to browse"}</div></>
          )}
        </div>
      )}
      {files.length > 0 && (
        <div className="mt-3 space-y-2">
          {files.map((file, i) => {
            const validation = fileValidation[`${file.name}-${file.size}`];
            const isValid = validation?.valid;
            const message = validation?.message;
            return (
              <div key={`${file.name}-${i}`} className={`flex items-center gap-3 rounded-md border px-3 py-2 transition-colors ${isValid === false ? "border-red-500/30 bg-red-500/[0.03]" : uploadStatus === "success" ? "border-[#14b8a6]/40 bg-[#14b8a6]/[0.04]" : isValid === true ? "border-[#14b8a6]/25 bg-[#14b8a6]/[0.02]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex-shrink-0">{getFileIcon(file)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/85 text-xs truncate">{file.name}</div>
                  <div className="flex flex-wrap items-baseline gap-1.5 mt-0.5">
                    <span className="text-white/35 text-[10px] whitespace-nowrap">{formatSize(file.size)}</span>
                    {message && (
                      <>
                        <span className="text-white/20 text-[9px] hidden sm:inline-block">&bull;</span>
                        <span className={`text-[9px] leading-tight font-medium flex-1 min-w-0 ${isValid === false ? "text-red-400" : "text-[#14b8a6]"}`}>{message}</span>
                      </>
                    )}
                    {uploadStatus === "success" && <span className="text-[#14b8a6] text-[10px] font-bold ml-auto">Uploaded ✓</span>}
                  </div>
                </div>
                {!isDisabled && (
                  <button type="button" onClick={() => removeFile(i)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-white/35 hover:text-white/75 transition flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SuccessScreen({ form }) {
  const isWaitlist = form.bookingDate === "Waitlist";
  return (
    <div className="text-center py-8">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#14b8a6]/15 border border-[#14b8a6]/40 mb-6">
        <Check className="w-10 h-10 text-[#14b8a6]" strokeWidth={3} />
      </div>
      <h3 className="font-display text-3xl md:text-4xl text-white mb-3">
        {isWaitlist ? "APPLICATION RECEIVED!" : "YOU'RE BOOKED!"}
      </h3>
      <p className="text-white/65 max-w-xl mx-auto mb-6">
        {isWaitlist ? (
          <>
            Thanks {form.firstName}. All slots are currently full (or none work for you), so you&apos;ve been successfully placed on our <span className="font-bold text-[#f5c451]">Priority Waitlist</span>. We&apos;ll review your application and email you immediately when a spot opens.
          </>
        ) : (
          <>
            Your application is in and your interview is confirmed. We&apos;ve sent the meeting details to your email.
          </>
        )}
      </p>
      {!isWaitlist && form.bookingDate && form.bookingTime && (
        <div className="inline-block px-6 py-4 rounded-sm border border-[#f5c451]/30 bg-[#f5c451]/[0.05]">
          <div className="text-[#f5c451] font-semibold">
            {form.bookingDate.toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
          <div className="text-white/70 mt-1">at {form.bookingTime} (Your local time)</div>
        </div>
      )}
    </div>
  );
}
